import React, { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, MapPin, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from '../hooks/use-toast';
import { useLang } from '../context/LangContext';

const SUCCESS_OVERLAY_MS = 4000;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const emptyForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  origin: '',
  destination: '',
  type: '',
  weight: '',
  details: '',
  website: '', // honeypot
};

const QuoteSection = React.forwardRef(({ t }, ref) => {
  const { lang } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const change = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/quote`,
        { ...form, lang },
        { timeout: 25000 }
      );
      if (!data?.ok) throw new Error('Unexpected response');

      toast({ title: t.quote.labels.success });
      setSubmitted(true);
      setForm(emptyForm);
      setTimeout(() => setSubmitted(false), SUCCESS_OVERLAY_MS);
    } catch (err) {
      const fallbackMsg =
        lang === 'en'
          ? 'Something went wrong. Please try again or email info@axaca.solutions.'
          : "Une erreur s'est produite. Réessayez ou écrivez à info@axaca.solutions.";
      const serverMsg = err?.response?.data?.detail;
      toast({
        title: lang === 'en' ? 'Submission failed' : 'Échec de l’envoi',
        description: serverMsg || fallbackMsg,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const L = t.quote.labels;

  return (
    <section
      id="quote"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#001a3d]/40 to-black" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#004aad]/15 blur-[140px] rounded-full" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-[#004aad]" />
            <span className="text-[#5b8def] text-xs tracking-[0.35em] font-semibold">
              {t.quote.tag}
            </span>
            <span className="h-px w-12 bg-[#004aad]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight">
            {t.quote.title}
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">{t.quote.subtitle}</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="relative rounded-3xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl p-8 md:p-10"
        >
          {submitted && (
            <div className="absolute inset-0 z-10 rounded-3xl bg-zinc-950/95 backdrop-blur flex flex-col items-center justify-center gap-4">
              <div className="h-16 w-16 rounded-full bg-[#004aad] flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              <p className="text-white text-lg text-center max-w-md px-6">
                {L.success}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <FormField label={L.name} required>
              <Input
                required
                value={form.name}
                onChange={(e) => change('name', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField label={L.company}>
              <Input
                value={form.company}
                onChange={(e) => change('company', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField label={L.email} required>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => change('email', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField label={L.phone}>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => change('phone', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField label={L.origin} icon={<MapPin className="h-4 w-4" />}>
              <Input
                placeholder="Montréal, QC"
                value={form.origin}
                onChange={(e) => change('origin', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField
              label={L.destination}
              icon={<MapPin className="h-4 w-4" />}
            >
              <Input
                placeholder="Toronto, ON"
                value={form.destination}
                onChange={(e) => change('destination', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
            <FormField label={L.type} icon={<Package className="h-4 w-4" />}>
              <Select value={form.type} onValueChange={(v) => change('type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-12">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  {t.quote.types.map((typ) => (
                    <SelectItem
                      key={typ}
                      value={typ}
                      className="focus:bg-[#004aad] focus:text-white"
                    >
                      {typ}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={L.weight}>
              <Input
                type="number"
                value={form.weight}
                onChange={(e) => change('weight', e.target.value)}
                className="bg-black/40 border-white/10 text-white h-12"
              />
            </FormField>
          </div>

          <FormField label={L.details} className="mt-5">
            <Textarea
              rows={4}
              value={form.details}
              onChange={(e) => change('details', e.target.value)}
              className="bg-black/40 border-white/10 text-white resize-none"
            />
          </FormField>

          {/* Honeypot — invisible to humans, bots fill it */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => change('website', e.target.value)}
              />
            </label>
          </div>

          <Button
            type="submit"
            disabled={sending}
            className="mt-6 h-14 px-8 bg-[#004aad] hover:bg-[#0057cc] text-white rounded-full text-base font-medium w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending
              ? lang === 'en'
                ? 'Sending…'
                : 'Envoi en cours…'
              : L.submit}
            {!sending && <Send className="ml-2 h-4 w-4" />}
          </Button>
        </form>
      </div>
    </section>
  );
});

const FormField = ({ label, required, children, icon, className = '' }) => (
  <div className={className}>
    <Label className="text-zinc-400 text-xs tracking-wide mb-2 flex items-center gap-2">
      {icon}
      {label}
      {required && <span className="text-[#5b8def]">*</span>}
    </Label>
    {children}
  </div>
);

export default QuoteSection;
